import dayjs from "dayjs";
import { LayoutTemplate } from "../LayoutTemplate";
import { MesesDoAnoEnum } from "../../../enums/mesesEnum";
import { Breakdown, card, num, pct, pctLabel } from "../GeneralTemplate/ui";
import { useCustomerSatisfaction } from "./useCustomerSatisfaction";

const fieldLabel =
  "text-xs font-semibold uppercase tracking-wider text-[#6B6B66]";
const selectCls =
  "h-11 w-full appearance-none rounded-[10px] border border-[#DAD9D4] bg-white pl-3.5 pr-9 text-sm";
const dec = (v?: number) => (v || 0).toFixed(1).replace(".", ",");
const ATALHOS = [
  { label: "Mês atual", date: () => dayjs() },
  { label: "Mês anterior", date: () => dayjs().subtract(1, "month") },
];

const Chevron = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6B6B66"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="pointer-events-none absolute right-3 top-3.5"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Stars = ({ color }: { color: string }) => (
  <div className="flex w-[136px] gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <svg key={i} width="24" height="24" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" />
      </svg>
    ))}
  </div>
);

const Meter = ({ value, color }: { value: number; color: string }) => (
  <div className="h-2 overflow-hidden rounded-full bg-[#E7E6E2]">
    <div
      className="h-full rounded-full"
      style={{ width: `${Math.min(100, value)}%`, background: color }}
    />
  </div>
);

export const CustomerSatisfactionTemplate = () => {
  const {
    mothOptions,
    yearOptions,
    filter,
    appliedFilter,
    handleFilter,
    pesquisa,
    setFilter,
    empresaOptions,
  } = useCustomerSatisfaction();
  const d = pesquisa[0];
  const geral = d?.notaGeral || 0;

  const kpis = [
    {
      title: "Questionários enviados",
      value: num(d?.quantidadeParticular),
      hint: "no mês selecionado",
    },
    {
      title: "Respostas",
      value: num(d?.quantidadeRespostas),
      hint: "questionários respondidos",
    },
  ];

  const criterios = [
    {
      label: "Atendimento ao cliente",
      tipo: "nota",
      value: dec(d?.notaAtendimentoCliente),
      suffix: "de 5",
      w: pct(d?.notaAtendimentoCliente, 5),
      color: "#1D1D1B",
    },
    {
      label: "Qualidade do serviço prestado",
      tipo: "nota",
      value: dec(d?.notaQualidadeServico),
      suffix: "de 5",
      w: pct(d?.notaQualidadeServico, 5),
      color: "#1D1D1B",
    },
    {
      label: "Agendamentos realizados no horário",
      tipo: "%",
      value: pctLabel(d?.porcentagemAgendamentosHorario || 0),
      suffix: "dos agendamentos",
      w: d?.porcentagemAgendamentosHorario || 0,
      color: "#E84E1B",
    },
    {
      label: "Recomendariam a empresa",
      tipo: "%",
      value: pctLabel(d?.porcentagemRecomendacao || 0),
      suffix: "dos respondentes",
      w: d?.porcentagemRecomendacao || 0,
      color: "#E84E1B",
    },
  ];

  return (
    <LayoutTemplate title="Satisfação do Cliente">
      <div className="flex flex-col gap-5 text-[#1D1D1B]">
        <span className="text-sm text-[#6B6B66]">
          {MesesDoAnoEnum[appliedFilter.mes]} de {appliedFilter.ano} ·{" "}
          {appliedFilter.empresa}
        </span>

        {/* Filtros */}
        <form
          onSubmit={handleFilter}
          className={`${card} flex flex-wrap items-end gap-5 py-4`}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="csat-mes" className={fieldLabel}>
              Mês
            </label>
            <div className="relative w-[180px]">
              <select
                id="csat-mes"
                className={selectCls}
                value={filter.mes}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, mes: +e.target.value }))
                }
              >
                {mothOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="csat-ano" className={fieldLabel}>
              Ano
            </label>
            <div className="relative w-[120px]">
              <select
                id="csat-ano"
                className={selectCls}
                value={filter.ano}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, ano: +e.target.value }))
                }
              >
                {yearOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={fieldLabel}>Atalhos</span>
            <div className="flex h-11 gap-0.5 rounded-[10px] bg-[#F1F0EC] p-1">
              {ATALHOS.map((a) => {
                const date = a.date();
                const mes = date.month() + 1;
                const ano = date.year();
                const active = filter.mes === mes && filter.ano === ano;
                return (
                  <button
                    key={a.label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter((prev) => ({ ...prev, mes, ano }))}
                    className={`rounded-md px-3 text-[13px] ${
                      active
                        ? "bg-white font-semibold shadow-sm"
                        : "font-medium text-[#4A4A46]"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-[1_1_360px] flex-col gap-1.5">
            <span className={fieldLabel}>Empresa</span>
            <div className="flex flex-wrap gap-2">
              {empresaOptions.map((opt) => {
                const active = filter.empresa === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setFilter((prev) => ({ ...prev, empresa: opt.value }))
                    }
                    className={`h-11 rounded-full border px-4 text-[13px] ${
                      active
                        ? "border-[#1D1D1B] bg-[#1D1D1B] font-semibold text-white"
                        : "border-[#DAD9D4] bg-white font-medium"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            className="h-11 rounded-[10px] bg-[#E84E1B] px-6 text-sm font-semibold text-white hover:bg-[#cf4316]"
          >
            Aplicar filtros
          </button>
        </form>

        {/* KPIs */}
        <section
          aria-label="Indicadores do mês"
          className="flex flex-wrap gap-4"
        >
          <div className="flex flex-[2_1_420px] flex-col gap-4 rounded-2xl bg-[#1D1D1B] p-5 text-white">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] font-medium text-[#C9C8C2]">
                Satisfação geral
              </span>
              <span className="rounded-full bg-[#3A1F14] px-2.5 py-0.5 text-xs font-semibold text-[#F4A988]">
                nota média do mês
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-5">
              <span className="flex items-baseline gap-2">
                <span className="text-[44px] font-bold leading-none tabular-nums tracking-tight">
                  {dec(geral)}
                </span>
                <span className="text-base text-[#C9C8C2]">de 5</span>
              </span>
              <div
                role="img"
                aria-label={`${dec(geral)} de 5 estrelas`}
                className="relative mb-1"
              >
                <Stars color="#3C3C3B" />
                <div
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: `${Math.min(100, pct(geral, 5))}%` }}
                >
                  <Stars color="#E84E1B" />
                </div>
              </div>
            </div>
            <span className="text-[13px] text-[#C9C8C2]">
              com base em{" "}
              <b className="font-semibold tabular-nums text-white">
                {num(d?.quantidadeRespostas)}
              </b>{" "}
              respostas
            </span>
          </div>

          {kpis.map((k) => (
            <div
              key={k.title}
              className={`${card} flex flex-[1_1_200px] flex-col gap-3.5`}
            >
              <span className="text-[13px] font-medium text-[#6B6B66]">
                {k.title}
              </span>
              <span className="text-4xl font-bold tabular-nums tracking-tight">
                {k.value}
              </span>
              <span className="text-[13px] text-[#6B6B66]">{k.hint}</span>
            </div>
          ))}

          <div className={`${card} flex flex-[1_1_200px] flex-col gap-3.5`}>
            <span className="text-[13px] font-medium text-[#6B6B66]">
              Engajamento
            </span>
            <span className="text-4xl font-bold tabular-nums tracking-tight">
              {pctLabel(d?.porcentagemRespostas || 0)}
            </span>
            <Meter value={d?.porcentagemRespostas || 0} color="#E84E1B" />
          </div>
        </section>

        {/* Avaliações + base de clientes */}
        <section aria-label="Avaliações" className="mb-8 flex flex-wrap gap-4">
          <div
            className={`${card} flex min-w-0 flex-[2_1_560px] flex-col gap-5`}
          >
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">Avaliação por critério</h2>
              <span className="text-[13px] text-[#6B6B66]">
                Notas de 0 a 5 e indicadores em % das respostas
              </span>
            </div>
            <div className="flex flex-wrap gap-4">
              {criterios.map((c) => (
                <div
                  key={c.label}
                  className="flex flex-[1_1_260px] flex-col gap-3 rounded-xl bg-[#F7F7F5] p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-[#4A4A46]">
                      {c.label}
                    </span>
                    <span className="rounded-full border border-[#E7E6E2] bg-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#6B6B66]">
                      {c.tipo}
                    </span>
                  </div>
                  <span className="flex items-baseline gap-1.5">
                    <span className="text-[28px] font-bold tabular-nums tracking-tight">
                      {c.value}
                    </span>
                    <span className="text-[13px] text-[#6B6B66]">
                      {c.suffix}
                    </span>
                  </span>
                  <Meter value={c.w} color={c.color} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex min-w-0 flex-[1_1_340px] flex-col [&>div]:flex-1">
            <Breakdown
              title="Atendimentos por tipo de cliente"
              parts={[
                {
                  label: "Particular",
                  value: d?.quantidadeParticular,
                  color: "#1D1D1B",
                },
                {
                  label: "Despachante",
                  value: d?.quantidadeDespachante,
                  color: "#E84E1B",
                },
                {
                  label: "Concessionária",
                  value: d?.quantidadeConcessionaria,
                  color: "#F4A988",
                },
              ]}
            >
              <div className="mt-auto flex flex-col gap-1.5 rounded-xl bg-[#F7F7F5] px-4 py-3.5">
                <span className="text-[13px] font-medium text-[#6B6B66]">
                  Taxa de resposta sobre a base total
                </span>
                <span className="text-[22px] font-bold tabular-nums">
                  {pctLabel(pct(d?.quantidadeRespostas, d?.quantidadeTotal))}
                </span>
                <span className="text-[13px] text-[#6B6B66]">
                  {num(d?.quantidadeRespostas)} respostas de{" "}
                  {num(d?.quantidadeTotal)} atendimentos
                </span>
              </div>
            </Breakdown>
          </div>
        </section>
      </div>
    </LayoutTemplate>
  );
};
